import classNames from "classnames";
import CiroTable from "../shared/CiroTable/CiroTable";
import CiroTableHeader, {
  CiroTableHeaderAlignEnum,
} from "../shared/CiroTable/CiroTableHeader";
import { gql } from "@apollo/client";
import { AutopilotPreviewContact_AutopilotSessionSavedContactPreview } from "./AutopilotPreviewContact";
import CiroTableRow from "../shared/CiroTable/CiroTableRow";
import CiroTableCell, {
  CiroTableCellAlignEnum,
} from "../shared/CiroTable/CiroTableCell";
import CiroOrgContactCellInfo from "../shared/CiroTable/CiroOrgContactCellInfo";
import { CiroOrgContactCellInfo_OrgContact } from "../shared/CiroTable/CiroOrgContactCellInfo";
import {
  AutopilotPreviewTable_ContactMatchPreviewFragment,
  CiroOrgContactCellInfo_OrgContactFragmentDoc,
} from "../../__generated__/graphql";
import AutopilotScoringResultTableCell from "../autopilotCuratedSearch/AutopilotScoringResultTableCell";
import AutopilotCriteriaMatchedCellInfo from "../automation/shared/AutopilotCriteriaMatchedCellInfo";
import LinkedInCompanyCellInfo, {
  LinkedInCompanyCellInfo_LinkedInCompany,
} from "../shared/CiroTable/LinkedInCompanyCellInfo";
import { useFragment as getFragmentData } from "../../__generated__";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import {
  HandThumbDownIcon as HandThumbDownIconOutline,
  HandThumbUpIcon as HandThumbUpIconOutline,
} from "@heroicons/react/24/outline";

import AutopilotPreviewContactFeedbackModal from "./AutopilotPreviewContactFeedbackModal";
import { useMemo, useState } from "react";
import { PreviewContactFeedbackMap } from "../../contexts/InternalAutopilotHydrationContext";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";

export const AutopilotPreviewTable_ContactMatchPreview = gql`
  fragment AutopilotPreviewTable_ContactMatchPreview on AutopilotSessionPreviewResponse {
    contacts {
      ...AutopilotPreviewContact_AutopilotSessionSavedContactPreview
      rationale
      org_contact {
        id
        company_name
        ...CiroOrgContactCellInfo_OrgContact
        linkedInCompany {
          ...LinkedInCompanyCellInfo_LinkedInCompany
        }
      }
      scoringCriteriaResults {
        criterion_fit
        reasoning
        criterion_id
        sources
      }
    }
    autopilotCuratedSearch {
      scoringCriteria {
        id
        label
        criterion
      }
    }
  }
  ${LinkedInCompanyCellInfo_LinkedInCompany}
  ${AutopilotPreviewContact_AutopilotSessionSavedContactPreview}
  ${CiroOrgContactCellInfo_OrgContact}
`;

interface IAutopilotPreviewTableProps {
  contactsMatchPreview:
    | AutopilotPreviewTable_ContactMatchPreviewFragment
    | null
    | undefined;
  previewContactFeedback: PreviewContactFeedbackMap;
  setPreviewContactFeedback: (feedback: PreviewContactFeedbackMap) => void;
}

const AutopilotPreviewTable = ({
  contactsMatchPreview,
  previewContactFeedback,
  setPreviewContactFeedback,
}: IAutopilotPreviewTableProps) => {
  const [showFeedbackModalForContact, setShowFeedbackModalForContact] =
    useState<{ id: number; name: string } | null>(null);

  const orderedContacts = useMemo(
    () =>
      contactsMatchPreview?.contacts.sort((a, b) => {
        const getScorePriority = (contact: typeof a) => {
          const results = contact.scoringCriteriaResults || [];
          const trueCount = results.filter(
            (r) => r.criterion_fit === true,
          ).length;
          const undefinedCount = results.filter(
            (r) => r.criterion_fit === undefined,
          ).length;
          const falseCount = results.filter(
            (r) => r.criterion_fit === false,
          ).length;
          // Return array of counts to compare in priority order
          return [trueCount, undefinedCount, falseCount];
        };

        const aScores = getScorePriority(a);
        const bScores = getScorePriority(b);

        // Compare each score type in priority order
        for (let i = 0; i < aScores.length; i++) {
          if (bScores[i] !== aScores[i]) {
            return bScores[i] - aScores[i];
          }
        }

        let valueA: boolean | null = null;
        let valueB: boolean | null = null;
        let index = 0;

        while (
          valueA === valueB &&
          index <
            contactsMatchPreview.autopilotCuratedSearch.scoringCriteria.length
        ) {
          const criterion =
            contactsMatchPreview.autopilotCuratedSearch.scoringCriteria[index];
          valueA =
            a.scoringCriteriaResults?.find(
              (r) => r.criterion_id === criterion.id,
            )?.criterion_fit ?? null;
          valueB =
            b.scoringCriteriaResults?.find(
              (r) => r.criterion_id === criterion.id,
            )?.criterion_fit ?? null;
          index++;
        }

        if (valueA === null && valueB === null) {
          return 0;
        }

        return valueA ? -1 : 1;
      }),
    [contactsMatchPreview],
  );

  if (
    !contactsMatchPreview ||
    contactsMatchPreview.contacts.length === 0 ||
    !orderedContacts
  ) {
    return (
      <div className={classNames("text-center", "text-neutral-600")}>
        No contacts found
      </div>
    );
  }

  const handlePositiveFeedbackClick = (contactId: number) => {
    const newFeedback = new Map(previewContactFeedback);
    const existingFeedback = newFeedback.get(contactId);
    if (existingFeedback?.isGoodFit) {
      newFeedback.delete(contactId);
    } else {
      newFeedback.set(contactId, {
        isGoodFit: true,
        isBadFit: false,
      });
    }
    setPreviewContactFeedback(newFeedback);
  };

  const handleNegativeFeedbackClick = (
    contactId: number,
    contactName: string,
  ) => {
    const newFeedback = new Map(previewContactFeedback);
    const existingFeedback = newFeedback.get(contactId);
    if (!existingFeedback?.isBadFit) {
      newFeedback.set(contactId, {
        isGoodFit: false,
        isBadFit: true,
      });
    }
    setPreviewContactFeedback(newFeedback);
    setShowFeedbackModalForContact({
      id: contactId,
      name: contactName,
    });
  };
  return (
    <>
      <CiroTable scrollable={true}>
        <thead className={classNames("table-header-group")}>
          <CiroTableRow>
            <CiroTableHeader isFirst={true} width="w-24">
              Name
            </CiroTableHeader>
            <CiroTableHeader>Company</CiroTableHeader>
            <CiroTableHeader align={CiroTableHeaderAlignEnum.CENTER}>
              Criteria matched
            </CiroTableHeader>
            {contactsMatchPreview.autopilotCuratedSearch.scoringCriteria.map(
              (criterion, idx) => (
                <CiroTableHeader
                  className={classNames("hidden", "xl:table-cell")}
                  key={criterion.id}
                  align={CiroTableHeaderAlignEnum.CENTER}
                >
                  {criterion.label}
                </CiroTableHeader>
              ),
            )}
            <CiroTableHeader
              align={CiroTableHeaderAlignEnum.CENTER}
              isLast={true}
            >
              Feedback
            </CiroTableHeader>
          </CiroTableRow>
        </thead>
        <tbody className={classNames("table-row-group")}>
          {orderedContacts.map((contactMatchPreview, idx) => {
            const isLast = idx === orderedContacts.length - 1;
            const contactFeedback = contactMatchPreview.org_contact.id
              ? previewContactFeedback.get(contactMatchPreview.org_contact.id)
              : null;
            // This shouldn't be necessary because the data is here, but this is the only way to satisfy typescript
            // without casting the type directly
            const orgContact = getFragmentData(
              CiroOrgContactCellInfo_OrgContactFragmentDoc,
              contactMatchPreview.org_contact,
            );

            const allSources = Array.from(
              new Set(
                contactMatchPreview.scoringCriteriaResults
                  ?.flatMap((result) => result.sources ?? [])
                  .filter(Boolean),
              ),
            );

            return (
              <CiroTableRow
                key={contactMatchPreview.org_contact.id}
                clickable={false}
              >
                <CiroTableCell isLastRow={isLast}>
                  <CiroOrgContactCellInfo orgContact={orgContact} />
                </CiroTableCell>
                <CiroTableCell isLastRow={isLast}>
                  <LinkedInCompanyCellInfo
                    linkedInCompany={
                      getFragmentData(
                        LinkedInCompanyCellInfo_LinkedInCompany,
                        contactMatchPreview.org_contact.linkedInCompany,
                      ) ?? null
                    }
                    companyName={
                      contactMatchPreview.org_contact.company_name ?? ""
                    }
                  />
                </CiroTableCell>
                <CiroTableCell
                  align={CiroTableCellAlignEnum.CENTER}
                  isLastRow={isLast}
                >
                  <AutopilotCriteriaMatchedCellInfo
                    scoringCriteria={
                      contactsMatchPreview.autopilotCuratedSearch
                        .scoringCriteria
                    }
                    scoringCriteriaResults={(
                      contactMatchPreview.scoringCriteriaResults ?? []
                    ).map((result) => ({
                      matched_criteria: result.criterion_fit,
                      scoring_criterion_id: result.criterion_id,
                      sources: result.sources,
                    }))}
                    sources={allSources}
                  />
                </CiroTableCell>
                {contactsMatchPreview.autopilotCuratedSearch.scoringCriteria.map(
                  (scoringCriterion) => {
                    const result =
                      contactMatchPreview.scoringCriteriaResults?.find(
                        (result) => result.criterion_id === scoringCriterion.id,
                      );
                    return (
                      <AutopilotScoringResultTableCell
                        changeBreakpointOnNavCollapse={false}
                        key={scoringCriterion.id}
                        result={
                          result
                            ? {
                                criterion_fit: result.criterion_fit,
                                reasoning: result.reasoning,
                                sources: result.sources,
                              }
                            : undefined
                        }
                        isLast={isLast}
                      />
                    );
                  },
                )}
                <CiroTableCell
                  isLastRow={isLast}
                  align={CiroTableCellAlignEnum.CENTER}
                >
                  <div
                    className={classNames("flex", "gap-2", "justify-center")}
                  >
                    {(!contactFeedback || contactFeedback?.isGoodFit) && (
                      <CiroButton
                        analyticsField="Autopilot preview contact feedback"
                        style={
                          contactFeedback
                            ? CiroButtonStyleEnum.BORDERLESS
                            : undefined
                        }
                        onClick={() =>
                          handlePositiveFeedbackClick(orgContact.id!)
                        }
                      >
                        <span
                          className={classNames(
                            "flex",
                            "gap-1",
                            "items-center",
                          )}
                        >
                          {contactFeedback ? (
                            <>
                              <HandThumbUpIcon
                                className={classNames("w-5", "h-5")}
                              />
                              Good Fit
                            </>
                          ) : (
                            <>
                              <HandThumbUpIconOutline
                                className={classNames("w-5", "h-5")}
                              />
                              Yes
                            </>
                          )}
                        </span>
                      </CiroButton>
                    )}
                    {(!contactFeedback || contactFeedback?.isBadFit) && (
                      <CiroButton
                        analyticsField="Autopilot preview contact feedback"
                        style={
                          contactFeedback
                            ? CiroButtonStyleEnum.BORDERLESS
                            : undefined
                        }
                        onClick={() =>
                          handleNegativeFeedbackClick(
                            orgContact.id!,
                            `${orgContact.first_name} ${orgContact.last_name}`,
                          )
                        }
                      >
                        <span
                          className={classNames(
                            "flex",
                            "gap-1",
                            "items-center",
                            { "text-red-500": contactFeedback },
                          )}
                        >
                          {contactFeedback ? (
                            <>
                              <HandThumbDownIcon
                                className={classNames("w-5", "h-5")}
                              />
                              Bad Fit
                            </>
                          ) : (
                            <>
                              <HandThumbDownIconOutline
                                className={classNames("w-5", "h-5")}
                              />
                              No
                            </>
                          )}
                        </span>
                      </CiroButton>
                    )}
                  </div>
                </CiroTableCell>
              </CiroTableRow>
            );
          })}
        </tbody>
      </CiroTable>
      <AutopilotPreviewContactFeedbackModal
        contactName={showFeedbackModalForContact?.name}
        contactId={showFeedbackModalForContact?.id}
        onClose={() => setShowFeedbackModalForContact(null)}
        previewContactFeedback={previewContactFeedback}
        setPreviewContactFeedback={setPreviewContactFeedback}
      />
    </>
  );
};

export default AutopilotPreviewTable;
